import { useState } from "preact/hooks";
import { ArrowRightIcon } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { PricingSection } from "./pricing.tsx";
import { automationSocialMeta } from "./socialMeta.ts";
import { talkToSkillRoute } from "./talkToSkill.tsx";
import { agencyRoute } from "./agency.tsx";

export const coderPrompt =
  `You are an autonomous AI Automation Assistant powered by prompt2bot.

You help users automate everyday workflows, connect their apps, and run tasks automatically.

Core responsibilities:
1. Email & Document Processing: Read incoming emails, extract key details from messages or attached invoices/PDFs, and organize information.
2. Spreadsheets & Databases: Add and update rows in Google Sheets, record client details, and log transaction metrics.
3. WhatsApp & Messaging Alerts: Send alerts, confirmations, and reminders to users or their team on WhatsApp.
4. Scheduled 24/7 Workflows: Run daily, weekly, or event-triggered automations reliably without manual intervention.`;

export const coderLaunchUrl = `${talkToSkillRoute}?url=${
  encodeURIComponent("tank:@uriva/p2b-coder")
}&name=Automation%20Coder&prompt=${encodeURIComponent(coderPrompt)}`;

const sectionClass = "py-16 px-4 sm:px-6 max-w-5xl mx-auto w-full";
const cardClass =
  "p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 shadow-sm";

const ZapIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  </svg>
);

const UnlockIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

const PiggyBankIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z" />
    <path d="M2 9v1c0 1.1.9 2 2 2h1" />
    <circle cx="16" cy="11" r="1" />
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const UserCheckIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

const InteractiveAutomationDemo = () => {
  const [activeScenario, setActiveScenario] = useState<
    "email_lead" | "invoice_drive" | "form_calendar" | "weekly_summary"
  >("email_lead");

  const scenarios = [
    {
      id: "email_lead" as const,
      label: "Email to Google Sheets & WhatsApp",
      userRequest:
        "Whenever I get an email from a new lead, extract their contact details, add a row to my Google Sheet, and send a summary to my WhatsApp.",
      steps: [
        {
          icon: "📩",
          title: "New Email Arrives",
          desc:
            "Agent reads incoming inquiry and extracts name, company, email, and request details.",
        },
        {
          icon: "📊",
          title: "Google Sheet Updated",
          desc:
            "Automatically logs a new row in your 'Inbound Leads 2026' spreadsheet with timestamp.",
        },
        {
          icon: "💬",
          title: "WhatsApp Notification",
          desc:
            "Sends a direct message to your phone: 'New lead from Sarah at Acme Corp saved to Sheet.'",
        },
      ],
      benefit:
        "Zero manual data entry or copy-pasting between your inbox and spreadsheet.",
    },
    {
      id: "invoice_drive" as const,
      label: "Invoice & Receipt Organizer",
      userRequest:
        "Whenever a vendor emails me an invoice PDF, save it to my Google Drive, log the amount and due date in my spreadsheet, and remind me on WhatsApp 2 days before payment.",
      steps: [
        {
          icon: "📎",
          title: "Invoice PDF Detected",
          desc:
            "Identifies incoming invoice attachment and reads the total balance and due date.",
        },
        {
          icon: "📁",
          title: "Saved to Google Drive",
          desc:
            "Files the PDF into your 'Invoices 2026' folder, neatly named by vendor and date.",
        },
        {
          icon: "⏰",
          title: "Scheduled Reminder",
          desc:
            "Schedules an automatic WhatsApp reminder 48 hours before the payment deadline.",
        },
      ],
      benefit:
        "Never lose a receipt, misplace an invoice, or miss a payment due date.",
    },
    {
      id: "form_calendar" as const,
      label: "Website Form to Calendar Booking",
      userRequest:
        "Whenever someone fills out the contact form on my website, check my Google Calendar for open slots, email them 3 available times, and confirm the appointment.",
      steps: [
        {
          icon: "🌐",
          title: "Form Submitted",
          desc:
            "Receives client submission with their requested service and timezone.",
        },
        {
          icon: "📅",
          title: "Calendar Checked",
          desc:
            "Inspects your real-time Google Calendar to find 3 conflict-free openings.",
        },
        {
          icon: "🤝",
          title: "Booking Confirmed",
          desc:
            "Emails the client options, reserves the slot on your calendar, and sends an invite.",
        },
      ],
      benefit: "Saves hours of back-and-forth scheduling emails every week.",
    },
    {
      id: "weekly_summary" as const,
      label: "Weekly Sales Summary",
      userRequest:
        "Every Monday at 9:00 AM, calculate last week's total sales from Stripe, find our top 3 products, and send a summary to our team WhatsApp group.",
      steps: [
        {
          icon: "⏰",
          title: "Scheduled 9:00 AM Run",
          desc:
            "Triggers automatically every Monday morning in the cloud while you're commuting.",
        },
        {
          icon: "💳",
          title: "Revenue Calculated",
          desc:
            "Pulls last week's Stripe transaction data and calculates total revenue.",
        },
        {
          icon: "👥",
          title: "Team WhatsApp Broadcast",
          desc:
            "Posts a clean, bulleted performance summary to your staff WhatsApp group.",
        },
      ],
      benefit:
        "Your team starts every Monday aligned without anyone compiling manual reports.",
    },
  ];

  const current = scenarios.find((s) => s.id === activeScenario) ??
    scenarios[0];

  return (
    <div className="w-full my-8 text-left max-w-4xl mx-auto">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveScenario(s.id)}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              activeScenario === s.id
                ? "bg-accent text-white shadow-md"
                : "bg-surface-raised-light dark:bg-surface-raised text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary border border-border-subtle-light dark:border-border-subtle"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Main Showcase Box */}
      <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-6 sm:p-8 shadow-md space-y-6">
        {/* User Prompt Box */}
        <div className="p-4 sm:p-5 rounded-xl bg-accent/10 border border-accent/20">
          <div className="text-xs uppercase font-bold tracking-wider text-accent mb-1.5 flex items-center gap-1.5">
            <span>You tell your AI agent:</span>
          </div>
          <p className="text-sm sm:text-base text-text-primary-light dark:text-text-primary font-medium italic leading-relaxed">
            "{current.userRequest}"
          </p>
        </div>

        {/* Step-by-Step Flow */}
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider font-semibold text-text-tertiary-light dark:text-text-tertiary">
            What happens automatically behind the scenes:
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {current.steps.map((step, idx) => (
              <div
                key={idx}
                className="p-4 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/60 dark:bg-surface-overlay/60 space-y-2 text-left"
              >
                <div className="text-2xl">{step.icon}</div>
                <div className="font-semibold text-sm text-text-primary-light dark:text-text-primary">
                  {step.title}
                </div>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Benefit Badge */}
        <div className="pt-4 border-t border-border-subtle-light dark:border-border-subtle flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-text-secondary-light dark:text-text-secondary">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-medium text-emerald-700 dark:text-emerald-400">
              {current.benefit}
            </span>
          </div>
          <span className="text-text-tertiary-light dark:text-text-tertiary">
            Zero complex node webs • Built in minutes
          </span>
        </div>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "How is this different from tools like Make.com, Zapier, or n8n?",
      a: "In Zapier or Make.com, you have to build complex visual diagrams by dragging boxes, connecting wires, and formatting JSON formulas. When an email looks slightly different or an API updates, your workflow breaks. With prompt2bot, you just tell your agent what you want in plain words ('whenever I get an invoice, save it and text me'). The AI writes and runs the code for you, handling changes intelligently.",
    },
    {
      q: "Can I move providers if I want to, or am I locked in?",
      a: "You are never locked in. Traditional tools trap you inside their proprietary visual diagram formats. With prompt2bot, your agent runs standard, transparent code and open standards. You own your prompts and logic, and can export or move anytime.",
    },
    {
      q: "Is it really cheaper than Zapier and Make.com?",
      a: "Yes, significantly. Zapier and Make charge you for every single step and operation inside a workflow. A 5-step automation on 100 entries consumes 500 tasks, forcing you into expensive tiers. prompt2bot offers predictable flat plans starting at $15/month with generous allowances and zero per-step penalties.",
    },
    {
      q: "How does this compare to developer tools like Claude Code?",
      a: "Claude Code is built for software engineers who work in a terminal. It requires developer knowledge, CLI setup, and burns through expensive frontier API tokens ($15 to $30+ per complex task). More importantly, local tools run directly on your personal computer, which requires downloading software and risks exposing your personal files. prompt2bot is designed for non-coders: you describe what you want in plain words over chat, nothing is ever downloaded to your computer, your PC is 100% safe, and it runs in the cloud for a fraction of the cost.",
    },
    {
      q: "Do I need to download or install software on my computer?",
      a: "No. You never need to download or install anything, and there is zero risk to your personal computer. Everything runs 100% in secure, isolated cloud containers.",
    },
    {
      q: "Which tools and apps can the automation connect to?",
      a: "Out of the box, it connects to Gmail, Google Sheets, Google Drive, Google Calendar, WhatsApp, Telegram, Stripe, and any website form or custom webhook.",
    },
    {
      q: "Does my computer need to stay on for automations to run?",
      a: "No. All automations run 24/7 on prompt2bot's secure cloud servers. They trigger automatically whether your computer is on, off, or you are asleep.",
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full text-left">
      <div className="text-center mb-12">
        <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
          Questions & Answers
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
          Answers about why prompt2bot is cleaner, cheaper, and free of vendor
          lock-in.
        </p>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 font-semibold text-base sm:text-lg"
              >
                <span>{faq.q}</span>
                <span
                  className={`text-xl transition-transform duration-200 text-text-tertiary ${
                    isOpen ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 sm:px-6 sm:pb-6 text-sm sm:text-base text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-border-subtle-light/60 dark:border-border-subtle/60 pt-4">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const AutomationPage = () => {
  usePageMeta({
    title: automationSocialMeta.title,
    description: automationSocialMeta.description,
    image: automationSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-screen text-center">
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-xs font-semibold mb-6">
          <ZapIcon className="w-4 h-4" />
          <span>No Complex Diagrams • Plain English Automation</span>
        </div>

        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-text-primary-light dark:text-text-primary max-w-4xl mx-auto leading-[1.1] mb-6">
          Automation using code, <br />
          <span className="text-accent">not using spaghetti diagrams.</span>
        </h1>

        <p className="text-lg sm:text-xl text-text-secondary-light dark:text-text-secondary max-w-2xl mx-auto leading-relaxed mb-8">
          Tired of 30-step Zapier, Make.com, or n8n workflows that break on the
          slightest change? Just tell your agent what you want in plain words —
          like{" "}
          <em>
            "whenever I get an email, update my spreadsheet and text me on
            WhatsApp"
          </em>{" "}
          — and let AI run the automation for you.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
          <a
            href={coderLaunchUrl}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <span>Launch Automation Coder</span>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
          <a
            href="#why-better"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary font-semibold text-base transition-all"
          >
            See the 3 advantages
          </a>
        </div>

        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          No credit card required. Free trial included.
        </p>

        {/* Plain English Interactive Demo Showcase */}
        <InteractiveAutomationDemo />
      </section>

      {/* The 3 Core Pillars Section */}
      <section id="why-better" className={sectionClass}>
        <div className="text-center mb-12">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Why It Beats Make & n8n
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            The Three Big Advantages
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-xl mx-auto">
            Traditional visual workflow builders are expensive, hard to
            maintain, and keep you locked in. prompt2bot is the clean
            alternative.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 text-left mb-12">
          {/* Pillar 1: Clean & Maintainable */}
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
              <SparklesIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Clean & Maintainable</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              No tangled spaghetti diagrams or brittle multi-node webs. When an
              email format changes or a spreadsheet adds a column, your agent
              adapts intelligently instead of breaking with red error boxes.
            </p>
          </div>

          {/* Pillar 2: Move Providers / Not Locked In */}
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4">
              <UnlockIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Zero Vendor Lock-In</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              With Make and Zapier, your entire workflow is trapped inside their
              proprietary visual format. With prompt2bot, you own your logic and
              instructions. You can export and move providers anytime.
            </p>
          </div>

          {/* Pillar 3: Cheapest */}
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-4">
              <PiggyBankIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Cheapest by Far</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              No punitive billing that charges you for every single step,
              filter, or task inside a workflow. Enjoy transparent flat plans
              starting at $15/month with generous monthly runs and zero per-step
              penalties.
            </p>
          </div>
        </div>

        {/* Side-by-side contrast */}
        <div className="grid md:grid-cols-2 gap-6 text-left">
          {/* n8n / Make Problems */}
          <div className="p-8 rounded-2xl border border-red-300/40 dark:border-red-900/40 bg-red-500/5 space-y-4">
            <div className="text-red-500 font-bold text-xs uppercase tracking-wider">
              The Disadvantages of n8n & Make.com
            </div>
            <h3 className="font-display text-2xl font-bold">
              Expensive, Fragile & Locked In
            </h3>
            <ul className="space-y-3.5 text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              <li className="flex items-start gap-2.5">
                <span className="text-red-500 font-bold mt-0.5">✕</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Expensive:
                  </strong>{" "}
                  Billing scales per operation and task. A simple 5-step
                  automation on 100 emails burns 500 tasks instantly, forcing
                  you into expensive enterprise tiers.
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-red-500 font-bold mt-0.5">✕</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Hard to Maintain:
                  </strong>{" "}
                  Tangled 30-node diagrams break silently whenever an email
                  structure, API field, or spreadsheet format changes slightly.
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-red-500 font-bold mt-0.5">✕</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Locked In:
                  </strong>{" "}
                  You are completely trapped in their proprietary visual graph.
                  Migrating elsewhere requires rebuilding all your automations
                  from scratch.
                </div>
              </li>
            </ul>
          </div>

          {/* prompt2bot Solution */}
          <div className="p-8 rounded-2xl border border-emerald-300/60 dark:border-emerald-700/60 bg-emerald-500/5 space-y-4">
            <div className="text-emerald-600 dark:text-emerald-400 font-bold text-xs uppercase tracking-wider">
              The prompt2bot Solution
            </div>
            <h3 className="font-display text-2xl font-bold">
              Cheaper, Maintainable & Portable
            </h3>
            <ul className="space-y-3.5 text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Cheapest:
                  </strong>{" "}
                  Flat, predictable monthly pricing from $15/mo with generous
                  allowances. No penalty fees for multi-step workflows.
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Clean & Maintainable:
                  </strong>{" "}
                  Describe your workflow in plain English. Your AI agent adapts
                  intelligently to data variations without crashing.
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                <div>
                  <strong className="text-text-primary-light dark:text-text-primary">
                    Can Move Providers:
                  </strong>{" "}
                  Open standards and clean instructions that you own. Export
                  your workflow logic or move providers whenever you want.
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Comparison against Claude Code & Local Tools */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Why Not Claude Code or Local Tools?
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Built for Non-Coders. Zero PC Risk.
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Tools like Claude Code give you an AI programmer, but they are made
            for software engineers, burn expensive tokens, and run directly on
            your personal computer.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 text-left">
          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <UserCheckIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Made for Non-Coders</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              No need to learn terminals, command lines, or git. You get all the
              output of an expert programmer simply by talking in everyday words
              over chat.
            </p>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-4">
              <PiggyBankIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Much Cheaper</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Claude Code burns through expensive frontier tokens per iteration,
              costing $15 to $30+ per complex task. prompt2bot offers
              predictable flat plans starting at $15/month.
            </p>
          </div>

          <div className={cardClass}>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
              <ShieldIcon className="w-6 h-6" />
            </div>
            <h3 className="font-semibold text-xl mb-2">Zero PC Exposure</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              No need to download anything or risk your personal computer.
              Everything runs 100% in secure cloud containers, keeping your
              personal files completely safe.
            </p>
          </div>
        </div>
      </section>

      {/* Direct Comparison Table */}
      <section className={sectionClass}>
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-3">
            How It Compares
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-sm sm:text-base max-w-lg mx-auto">
            Side-by-side comparison with traditional visual workflow builders.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised shadow-sm text-left">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/50 dark:bg-surface-overlay/50">
                <th className="p-4 sm:p-5 font-semibold text-text-secondary-light dark:text-text-secondary">
                  Factor
                </th>
                <th className="p-4 sm:p-5 font-semibold text-text-secondary-light dark:text-text-secondary">
                  Zapier / Make.com / n8n
                </th>
                <th className="p-4 sm:p-5 font-semibold text-accent">
                  prompt2bot Coder
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle-light/60 dark:divide-border-subtle/60">
              <tr>
                <td className="p-4 sm:p-5 font-medium">Workflow authoring</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Dragging 20+ boxes, wires & manual field mapping
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Just describe in plain words ("whenever X, do Y")
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">
                  Maintenance & reliability
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Fragile: breaks if an email or form changes format
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Clean & self-healing: AI adapts to data changes
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Vendor lock-in</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  100% locked into their proprietary visual graph
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Zero lock-in: you own your logic and can move providers
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Technical barrier</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Requires understanding data mapping, triggers & JSON logic
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Built for non-coders: talk naturally in everyday language
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">
                  Installation & PC safety
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Cloud dashboard (or self-hosted server maintenance)
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Zero downloads: runs in cloud with zero risk to your PC
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Cost & pricing model</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Charges per step and task (costs skyrocket quickly)
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Cheapest: flat plans from $15/mo with no per-step fees
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Messaging & alerts</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Requires separate SMS or webhook connectors
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Direct WhatsApp, Telegram, and Email integration
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Pre-built Agency Specialists Callout */}
      <section className="py-4 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center">
        <div className="p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/60 dark:bg-surface-overlay/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-left shadow-xs">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-text-primary-light dark:text-text-primary mb-1">
              Need pre-built specialist agents instead?
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              Explore 250+ pre-configured AI roles for sales, customer
              operations, analytics, and marketing in our Agency catalog.
            </p>
          </div>
          <a
            href={agencyRoute}
            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-surface-raised-light dark:bg-surface-raised hover:bg-surface-overlay-light dark:hover:bg-surface-overlay border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary font-semibold text-sm transition-all shadow-xs"
          >
            <span>Browse Agency Roster</span>
            <ArrowRightIcon className="w-4 h-4 text-accent" />
          </a>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={sectionClass}>
        <PricingSection />
      </section>

      {/* FAQ Section */}
      <FaqSection />

      {/* Bottom CTA Banner */}
      <section className="py-20 px-4 sm:px-6 max-w-4xl mx-auto w-full text-center">
        <div className="p-10 sm:p-14 rounded-3xl bg-gradient-to-b from-accent-light/10 via-surface-raised-light to-surface-raised-light dark:from-accent/15 dark:via-surface-raised dark:to-surface-raised border border-accent/20 shadow-xl space-y-6">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            Stop wrestling with visual nodes.
          </h2>
          <p className="text-base sm:text-lg text-text-secondary-light dark:text-text-secondary max-w-xl mx-auto leading-relaxed">
            Just describe your workflow in plain words. Your AI agent handles
            the rest and keeps your everyday tasks running smoothly.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <a
              href={coderLaunchUrl}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span>Launch Automation Coder in one click</span>
              <ArrowRightIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
